import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import axios from 'axios';
import { useUser } from '../../utils/userContext';
import { Space, Tooltip, Button, Dropdown, Menu, Modal, Form, Input, message, Row, Col, Select } from 'antd';
import {
    DownloadOutlined,
    MailOutlined,
    EyeOutlined,
    EditOutlined,
    CaretDownOutlined,
    CheckOutlined,
    CloseOutlined,
    UploadOutlined
} from '@ant-design/icons';

function Invoices() {
    const [invoices, setInvoices] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);
    const [updatingPayment, setUpdatingPayment] = useState(false);
    const [updatingInvoiceId, setUpdatingInvoiceId] = useState(null);
    const [emailForm] = Form.useForm();
    const [uploadForm] = Form.useForm();
    const [sendingEmail, setSendingEmail] = useState(false);
    const [uploadingInvoice, setUploadingInvoice] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const token = localStorage.getItem("token");
    const { user } = useUser();
    const [employees, setEmployees] = useState([]);
    const [consultants, setConsultants] = useState([]);

    useEffect(() => {
        const fetchInvoices = async () => {
            setLoading(true);
            try {
                const URL = process.env.REACT_APP_BE_URL + '/getConsultantInvoices';
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.status === 200) {
                    console.log("API Response:", response.data.invoices);
                    setInvoices(response.data.invoices.map(inv => ({
                        ...inv,
                        invoiceId: inv.invoiceNo,
                        consultant: inv.consultantEmail,
                        consultantName: inv.agency,
                        month: moment(inv.startDate).format("MMMM"),
                        year: moment(inv.startDate).year(),
                        amount: inv.amount || 0,
                        status: inv.status || "pending",
                        generatedDate: inv.startDate,
                        agency: inv.agency,
                        agencyEmail: inv.agencyEmail,
                        invoiceFile: inv.invoiceFile,
                        startDate: inv.startDate,
                        endDate: inv.endDate,
                        generatingAgencyName: inv.generatingAgencyName,
                        consultantAgency: inv.consultantAgency,
                    })));
                }
            } catch (error) {
                console.error("Failed to fetch invoices:", error);
                message.error("No Invoices Found");
            } finally {
                setLoading(false);
            }
        };
        fetchInvoices();
    }, []);

    useEffect(() => {
        const fetchOrganisationVendorDetails = async () => {
            try {
                const url = `${process.env.REACT_APP_BE_URL}/getpartnervendoronDetails`;
                const response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                
                if (response.status === 200 && response.data) {
                    const activeEmployees = response.data.partnerVendorDetails || [];
                    setEmployees(activeEmployees);
                } else {
                    setEmployees([]); // Set empty array instead of null
                }
            } catch (error) {
                console.error('Failed to fetch organisation/vendor details:', error);
                setEmployees([]); // Set empty array instead of null
            }
        };
        fetchOrganisationVendorDetails();
        fetchEmployeesList();
    }, [token]);

    // Fetch consultants for Consultant Email dropdown
    const fetchEmployeesList = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_BE_URL}/getEmployeeMasterReport`;
                    const response = await axios.get(url, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
            const list = response?.data?.onboardEmployeeDetails || [];
            const active = list.filter(
                (e) => e.Status === "Active" && e.EmployeeType === "Consultant"
            );
            setConsultants(active);
        } catch (err) {
            // handle error as needed
        } finally {
            setLoading(false);
        }
    };

    const assignToOptions = useMemo(() => {
        if (!employees) return [];
        
        return employees
            .filter(emp => emp?.UserType?.toLowerCase() === 'vendor')
            .map((emp) => ({
                value: emp.PartnerName,
                label: `${emp.PartnerName}`,
            }));
    }, [employees]);


    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file type
            const allowedTypes = [
                "application/pdf",
                "image/jpeg",
                "image/png",
                "image/jpg",
            ];
            if (!allowedTypes.includes(file.type)) {
                message.error("Please upload only PDF, JPG, or PNG files");
                return;
            }

            // Check file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                message.error("File size should be less than 5MB");
                return;
            }

            setUploadedFile(file);
        }
    };

    const handleUploadInvoiceClick = () => {
        setIsUploadModalVisible(true);
        uploadForm.resetFields();
        setUploadedFile(null);
    };

    const handleUploadModalCancel = () => {
        setIsUploadModalVisible(false);
        uploadForm.resetFields();
        setUploadedFile(null);
    };

    // Auto-fill Vendor Email when Vendor is selected
    const handleUploadFormValuesChange = (changedValues, allValues) => {
        if (changedValues.agency) {
            // Find the selected vendor's email
            const selectedVendor = employees.find(
                emp => emp.PartnerName === changedValues.agency
            );
            if (selectedVendor && selectedVendor.Email) {
                uploadForm.setFieldsValue({ agencyEmail: selectedVendor.Email });
            } else {
                uploadForm.setFieldsValue({ agencyEmail: '' });
            }
        }
        if (changedValues.consultantEmail) {
            // Find the selected consultant's email
            const selectedConsultant = consultants.find(
                c => c.OfficialEmail === changedValues.consultantEmail
            );
            if (selectedConsultant && selectedConsultant.OfficialEmail) {
                uploadForm.setFieldsValue({ consultantEmail: selectedConsultant.OfficialEmail });
            } else {
                uploadForm.setFieldsValue({ consultantEmail: '' });
            }
        }
    };

    const handleUploadInvoice = async (values) => {
        if (!uploadedFile) {
            message.error("Please select a file to upload");
            return;
        }

        setUploadingInvoice(true);
        try {
            const URL = process.env.REACT_APP_BE_URL + '/invoiceupload';

            // Find the selected vendor's email (for safety)
            let agencyEmail = values.agencyEmail;
            if (!agencyEmail) {
                const selectedVendor = employees.find(emp => emp.PartnerName === values.agency);
                agencyEmail = selectedVendor && selectedVendor.Email ? selectedVendor.Email : '';
            }

            // Prepare FormData
            const formData = new FormData();
            formData.append("InvoiceNo", values.invoiceNo);
            formData.append("Agency", values.agency);
            formData.append("AgencyEmail", agencyEmail);
            formData.append("ConsultantEmail", values.consultantEmail);
            formData.append("StartDate", moment(values.startDate).format("YYYY-MM-DD[T]00:00:00"));
            formData.append("EndDate", moment(values.endDate).format("YYYY-MM-DD[T]00:00:00"));
            formData.append("Status", "Pending");
            formData.append("invoiceUpload", uploadedFile);

            const response = await axios.post(URL, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.data.status === "Success" && response.data.statusCode === "200 OK") {
                message.success(response.data.message);
                setIsUploadModalVisible(false);
                uploadForm.resetFields();
                setUploadedFile(null);

                // Refresh the invoice list
                const invoicesResponse = await axios.get(process.env.REACT_APP_BE_URL + '/getConsultantInvoices', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (invoicesResponse.status === 200) {
                    setInvoices(invoicesResponse.data.invoices.map(inv => ({
                        ...inv,
                        invoiceId: inv.invoiceNo,
                        consultant: inv.consultantEmail,
                        consultantName: inv.agency,
                        month: moment(inv.startDate).format("MMMM"),
                        year: moment(inv.startDate).year(),
                        amount: inv.amount || 0,
                        status: inv.status || "pending",
                        generatedDate: inv.startDate,
                        agency: inv.agency,
                        agencyEmail: inv.agencyEmail,
                        invoiceFile: inv.invoiceFile,
                        startDate: inv.startDate,
                        endDate: inv.endDate,
                        generatingAgencyName: inv.generatingAgencyName,
                        consultantAgency: inv.consultantAgency,
                    })));
                }
            } else {
                message.error("Failed to upload invoice: Unexpected response");
            }
        } catch (error) {
            console.error("Upload invoice error:", error);
            message.error(`Failed to upload invoice: ${error.message || "Unknown error"}`);
        } finally {
            setUploadingInvoice(false);
        }
    };

    const filteredInvoices = useMemo(() => {
        return invoices.filter(inv =>
            inv.invoiceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
            inv.consultant.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (inv.consultantName && inv.consultantName.toLowerCase().includes(searchQuery.toLowerCase())) ||
            inv.month.toLowerCase().includes(searchQuery.toLowerCase()) ||
            String(inv.year).includes(searchQuery) ||
            String(inv.amount).includes(searchQuery) ||
            inv.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (inv.agency && inv.agency.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (inv.agencyEmail && inv.agencyEmail.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [searchQuery, invoices]);

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredInvoices.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredInvoices.length / recordsPerPage);

    const paginate = pageNumber => setCurrentPage(pageNumber);

    //     setGeneratingInvoice(true);
    //     try {
    //         if (invoice && invoice.invoiceFile) {
    //             const response = await fetch(invoice.invoiceFile, {
    //                 headers: {
    //                     Authorization: `Bearer ${token}`,
    //                 },
    //             });
    //             if (!response.ok) {
    //                 throw new Error('Failed to fetch invoice file');
    //             }
    //             const blob = await response.blob();
    //             const url = URL.createObjectURL(blob);
    //             const link = document.createElement("a");
    //             link.href = url;
    //             link.download = `invoice_${invoice.invoiceId}.pdf`;
    //             document.body.appendChild(link);
    //             link.click();
    //             document.body.removeChild(link);
    //             URL.revokeObjectURL(url);
    //             message.success(`${invoice.invoiceId} downloaded successfully!`);
    //         } else {
    //             message.error(`No invoice file available for ${invoice?.invoiceId || 'this invoice'}`);
    //         }
    //     } catch (error) {
    //         console.error("Invoice download error:", error);
    //         message.error(`Failed to download invoice: ${error.message || "Unknown error"}`);
    //     } finally {
    //         setGeneratingInvoice(false);
    //     }
    // };

    const handleGenerateInvoice = (invoice) => {
        if (invoice && invoice.invoiceFile) {
            try {
                // Open PDF in new tab
                const newWindow = window.open();
                if (newWindow) {
                    newWindow.document.write(`
          <iframe 
            src="${invoice.invoiceFile}" 
            width="100%" 
            height="100%" 
            style="
              border: none;
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              width: 100%;
              height: 100%;
            "
          ></iframe>
        `);
                    message.success(`Invoice ${invoice.invoiceId} opened in new tab`);
                } else {
                    message.error("Pop-up blocked. Please allow pop-ups for this site.");
                }
            } catch (error) {
                console.error("Error opening invoice:", error);
                message.error(`Failed to open invoice: ${error.message || "Unknown error"}`);
            }
        } else {
            message.error(`No invoice file available for ${invoice?.invoiceId || "this invoice"}`);
        }
    };

    const handleEmailClick = (invoice) => {
        setSelectedInvoice(invoice);
        setIsEmailModalVisible(true);
        emailForm.resetFields();
    };

    const handleEmailModalCancel = () => {
        setIsEmailModalVisible(false);
        emailForm.resetFields();
    };

    const handleEmailSend = async (values) => {
        setSendingEmail(true);
        try {
            // Prepare email details
            const subject = `Invoice ${selectedInvoice.invoiceId} from Workwise`;
            const body = `Please find the details for Invoice No: ${selectedInvoice.invoiceId}\nVendor: ${selectedInvoice.agency}\nVendor Email: ${selectedInvoice.agencyEmail}\nConsultant Email: ${selectedInvoice.consultant}\nMonth: ${selectedInvoice.month}\nYear: ${selectedInvoice.year}\nStatus: ${selectedInvoice.status}\n\nPlease attach the downloaded invoice file to this email.`;

            // Attempt to download the invoice file if available
            if (selectedInvoice && selectedInvoice.invoiceFile) {
                try {
                    const response = await fetch(selectedInvoice.invoiceFile, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    if (response.ok) {
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `invoice_${selectedInvoice.invoiceId}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                    }
                } catch (fetchError) {
                    console.error("Failed to fetch invoice file:", fetchError);
                    // Continue to open email client even if fetch fails
                }
            } else {
                console.warn(`No invoice file available for ${selectedInvoice?.invoiceId || 'this invoice'}`);
            }

            // Open email client with pre-filled details
            window.location.href = `mailto:${values.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            setIsEmailModalVisible(false);
            emailForm.resetFields();
        } catch (error) {
            console.error("Email sending error:", error);
            message.error(`Failed to prepare email: ${error.message || "Unknown error"}`);
        } finally {
            setSendingEmail(false);
        }
    };

    const handleViewClick = (invoice) => {
        setSelectedInvoice(invoice);
        setIsViewModalVisible(true);
    };

    const handleViewModalCancel = () => {
        setIsViewModalVisible(false);
        setSelectedInvoice(null);
    };
    const handleStatusUpdate = async (invoice, newStatus) => {
        setUpdatingPayment(true);
        setUpdatingInvoiceId(invoice.id);
        try {
      // Prepare payload for API
      const formData = new FormData();
            formData.append("InvoiceNo", invoice.invoiceNo);
            formData.append("Agency", invoice.agency);
            formData.append("AgencyEmail", invoice.agencyEmail);
            formData.append("ConsultantEmail", invoice.consultantEmail);
            formData.append("StartDate", moment(invoice.startDate).format("YYYY-MM-DD[T]00:00:00"));
            formData.append("EndDate", moment(invoice.endDate).format("YYYY-MM-DD[T]00:00:00"));
            formData.append("Status", newStatus);
            formData.append("UpdatedBy", user?.EmployeeId);
            formData.append("invoiceUpload", uploadedFile);

      const URL = process.env.REACT_APP_BE_URL + "/invoiceupload";
      
      const response = await axios.post(URL, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.data.status === "Success") {
        setInvoices((prevInvoices) =>
          prevInvoices.map((inv) =>
            inv.invoiceId === invoice.invoiceId ? { ...inv, status: newStatus } : inv
          )
        );
        message.success(response.data.message || `Status updated to ${newStatus} successfully!`);
      } else {
        message.error(response.data.message || `Failed to update status: ${newStatus}`);
      }
    } catch (error) {
      console.error("Status update error:", error);
      message.error(`Failed to update status: ${error.response?.data?.message || error.message || "Unknown error"}`);
    } finally {
      setUpdatingPayment(false);
      setUpdatingInvoiceId(null);
    }
    };
    // Actions column render function
    const renderActions = (invoice) => {
        const isPaid = invoice.status === "paid";
        const isRejected = invoice.status === "reject";
        const isUpdating = updatingPayment && updatingInvoiceId === invoice.id;

        return (
            <Space size={6}>
                {/* Download Button */}
                <Tooltip title="Downlaod Invoice">
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        size="small"
                        loading={generatingInvoice && selectedInvoice?.invoiceId === invoice.invoiceId}
                        style={{
                            backgroundColor: "#004f7c",
                            borderColor: "#004f7c",
                            fontSize: "12px",
                            padding: "4px 8px",
                            height: "28px",
                            minWidth: "28px"
                        }}
                        onClick={() => handleGenerateInvoice(invoice)}
                    />
                </Tooltip>

                {/* Email Button */}
                <Tooltip title="Send Email">
                    <Button
                        type="default"
                        icon={<MailOutlined />}
                        size="small"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEmailClick(invoice);
                            return false;
                        }}
                        style={{
                            borderColor: "#004f7c",
                            color: "#004f7c",
                            fontSize: "12px",
                            padding: "4px 8px",
                            height: "28px",
                            minWidth: "28px"
                        }}
                    />
                </Tooltip>

                {/* View Button */}
                <Tooltip
                    title={
                        isPaid
                            ? "View Paid Invoice"
                            : isRejected
                                ? "View Rejected Invoice"
                                : "View Invoice"
                    }
                >
                    <Button
                        type="default"
                        icon={<EyeOutlined />}
                        size="small"
                        onClick={() => handleViewClick(invoice)}
                        style={{
                            borderColor: isPaid
                                ? "#52c41a"
                                : isRejected
                                    ? "#ff4d4f"
                                    : "#004f7c",
                            color: isPaid
                                ? "#52c41a"
                                : isRejected
                                    ? "#ff4d4f"
                                    : "#004f7c",
                            backgroundColor: isPaid
                                ? "#f6ffed"
                                : isRejected
                                    ? "#fff2f0"
                                    : "transparent",
                            fontSize: "12px",
                            padding: "4px 8px",
                            height: "28px",
                            minWidth: "28px"
                        }}
                    />
                </Tooltip>

                {/* Status Update Dropdown */}
                {!isPaid && invoice.status !== "reject" && (
                    <Tooltip title="Update Payment Status">
                        <Dropdown
                            overlay={getStatusDropdownMenu(invoice)}
                            trigger={["click"]}
                            placement="bottomRight"
                            disabled={isUpdating}
                        >
                            <Button
                                type="default"
                                loading={isUpdating}
                                size="small"
                                style={{
                                    borderColor: "#004f7c",
                                    color: "#004f7c",
                                    fontSize: "11px",
                                    padding: "4px 6px",
                                    height: "28px",
                                    minWidth: "28px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                {!isUpdating && (
                                    <>
                                        <EditOutlined style={{ fontSize: "11px", marginRight: "2px" }} />
                                        <CaretDownOutlined style={{ fontSize: "10px" }} />
                                    </>
                                )}
                            </Button>
                        </Dropdown>
                    </Tooltip>
                )}
            </Space>
        );
    };

    const getStatusDropdownMenu = (invoice) => (
        <Menu
            onClick={({ key }) => handleStatusUpdate(invoice, key)}
            style={{
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                border: "1px solid #f0f0f0",
            }}
        >
            <Menu.Item
                key="paid"
                style={{
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "14px",
                    fontWeight: "500",
                }}
            >
                <CheckOutlined style={{ color: "#52c41a", marginRight: "8px" }} />
                Paid
            </Menu.Item>
            <Menu.Item
                key="reject"
                style={{
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "14px",
                    fontWeight: "500",
                }}
            >
                <CloseOutlined style={{ color: "#ff4d4f", marginRight: "8px" }} />
                Reject
            </Menu.Item>
        </Menu>
    );
    return (
        <div className="content-page">
            <div className="container-fluid">
                <div className="row mb-2">
                    <div className="col">
                        <h3 className="title mb-2">Invoices</h3>
                    </div>
                    <div className="col-auto">
                        <Tooltip title="Upload invoice file">
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                onClick={handleUploadInvoiceClick}
                                style={{
                                    backgroundColor: "#004f7c",
                                    borderColor: "#004f7c",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                Upload Invoice
                            </Button>
                        </Tooltip>
                    </div>
                </div>
                <div className="row align-items-center justify-content-center">
                    <div className="col-5">
                        <div className="input-group mb-0">
                            <input
                                type="search"
                                className="form-control search-input"
                                placeholder="Keyword search..."
                                value={searchQuery}
                                onChange={e => {
                                    setSearchQuery(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            <div className="input-group-append">
                                <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                                    <i className="ri-menu-search-line"></i>
                                </button>
                            </div>
                        </div>
                        <small className="form-text text-muted text-center mt-0">
                            <i className="ri-information-2-line"></i> Search by Invoice, Vendor,Vendor Email, Consultant Email, Status
                        </small>
                    </div>
                </div>
                <div className="row mt-3">
                    <div className="col-md-12">
                        <div className="table-responsive asset-information">
                            <table className="table grid">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Invoice No</th>
                                        <th>Vendor</th>
                                        <th>Vendor Email</th>
                                        <th>Consultant Email</th>
                                        <th>Month</th>
                                        <th>Year</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={8} className="text-center py-4">
                                                <span className="loader"></span>
                                            </td>
                                        </tr>
                                    ) : currentRecords.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="text-center py-4">
                                                <div className="no-data-wrapper">
                                                    <div className="no-data-found">
                                                        <i className="ri-file-damage-fill"></i>
                                                        <div className="bounce"><i className="ri-search-eye-line"></i></div>
                                                        <p>No Invoices Found</p>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        currentRecords.map((inv, idx) => (
                                            <tr key={inv.invoiceId}>
                                                <td>{inv.invoiceId}</td>
                                                <td>{inv.agency}</td>
                                                <td
                                                    style={{
                                                        maxWidth: '150px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                    title={inv.agencyEmail} // full email on hover
                                                >
                                                    {inv.agencyEmail}
                                                </td>

                                                <td
                                                    style={{
                                                        maxWidth: '150px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                    title={inv.consultant} // full text on hover
                                                >
                                                    {inv.consultant}
                                                </td>

                                                <td>{inv.month}</td>
                                                <td>{inv.year}</td>
                                                <td>
                                                <span
                                                    className={`badge border status-badge ${
                                                    inv.status === "Paid"
                                                        ? "border-success text-success"
                                                        : inv.status === "Pending"
                                                        ? "border-warning text-warning"
                                                        : inv.status === "Reject" || inv.status === "reject"
                                                            ? "border-danger text-danger"
                                                            : "border-secondary text-secondary"
                                                    }`}
                                                >
                                                    {inv.status}
                                                </span>
                                                </td>
                                                <td>
                                                    {renderActions(inv)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                {/* Pagination */}
                <div className="col-md-12">
                    <div className="container-fluid">
                        <div className="row border-top pt-3">
                            <div className="col-auto">
                                <p className="mt-1 mb-0">Records</p>
                            </div>
                            <div className="col-1.2 record-drop">
                                <select className="form-control" value={recordsPerPage} onChange={e => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="15">15</option>
                                    <option value="20">20</option>
                                    <option value="25">25</option>
                                </select>
                            </div>
                            <div className="col">
                                <nav>
                                    <ul className="pagination justify-content-center">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                <i className="ri-arrow-left-double-line"></i>
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                <i className="ri-arrow-left-s-line"></i>
                                            </button>
                                        </li>
                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                    {index + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                <i className="ri-arrow-right-s-line"></i>
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                <i className="ri-arrow-right-double-fill"></i>
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                            <div className="col-auto">
                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Email Modal */}
                <Modal
                    title="Send Invoice via Email"
                    visible={isEmailModalVisible}
                    onCancel={handleEmailModalCancel}
                    footer={null}
                >
                    <Form
                        form={emailForm}
                        layout="vertical"
                        onFinish={handleEmailSend}
                    >
                        <Form.Item
                            name="email"
                            label="Recipient Email"
                            rules={[
                                { required: true, message: "Please enter recipient email" },
                                { type: "email", message: "Please enter a valid email" },
                            ]}
                        >
                            <Input placeholder="Enter email address" />
                        </Form.Item>

                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={sendingEmail}
                                    style={{ backgroundColor: "#004f7c", borderColor: "#004f7c" }}
                                >
                                    Send
                                </Button>
                                <Button onClick={handleEmailModalCancel}>Cancel</Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>

                {/* View Modal */}
                <Modal
                    title={`Invoice Details - ${selectedInvoice?.invoiceId}`}
                    visible={isViewModalVisible}
                    onCancel={handleViewModalCancel}
                    width={800}
                    footer={[
                        <Button key="close" onClick={handleViewModalCancel}>
                            Close
                        </Button>,
                        <Button
                            key="download"
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => handleGenerateInvoice(selectedInvoice)}
                            style={{ backgroundColor: "#004f7c", borderColor: "#004f7c" }}
                        >
                            Download
                        </Button>,
                    ]}
                >
                    {selectedInvoice && (
                        <div style={{ height: "600px", overflow: "auto" }}>
                            {selectedInvoice.invoiceFile ? (
                                <iframe
                                    src={selectedInvoice.invoiceFile}
                                    width="100%"
                                    height="100%"
                                    title={`Invoice ${selectedInvoice.invoiceId}`}
                                    style={{ border: "none" }}
                                />
                            ) : (
                                <p>No invoice file available for this invoice.</p>
                            )}
                        </div>
                    )}
                </Modal>

                {/* Upload Invoice Modal */}
                <Modal
                    title="Upload Invoice"
                    visible={isUploadModalVisible}
                    onCancel={handleUploadModalCancel}
                    footer={null}
                    width={600}
                >
                    <Form
                        form={uploadForm}
                        layout="vertical"
                        onFinish={handleUploadInvoice}
                        requiredMark={false}
                        onValuesChange={handleUploadFormValuesChange}
                    >
                        <Form.Item
                            name="agency"
                            label={
                                <span>
                                    Vendor <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: "Please select a vendor" }
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder="Select vendor"
                                allowClear
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                onChange={value => {
                                    const selectedVendor = employees.find(emp => emp.PartnerName === value);
                                    if (selectedVendor && selectedVendor.Email) {
                                        uploadForm.setFieldsValue({ agencyEmail: selectedVendor.Email });
                                    } else {
                                        uploadForm.setFieldsValue({ agencyEmail: '' });
                                    }
                                }}
                            >
                                {assignToOptions.map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="agencyEmail"
                            label={
                                <span>
                                    Vendor Email <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: "Vendor email will be auto-filled" },
                                { type: "email", message: "Please enter a valid email" },
                            ]}
                        >
                            <Input
                                placeholder="Vendor email will be auto-filled"
                                disabled
                                style={{
                                    backgroundColor: 'white',
                                    color: 'black',
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="consultantEmail"
                            label={
                                <span>
                                    Consultant Email <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: "Please select consultant email" },
                                { type: "email", message: "Please enter a valid email" },
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder="Select consultant email"
                                allowClear
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {consultants.map(consultant => (
                                    <Select.Option key={consultant.OfficialEmail} value={consultant.OfficialEmail}>
                                        {consultant.OfficialEmail}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="invoiceNo"
                            label={
                                <span>
                                    Invoice Number <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: "Please enter invoice number" },
                                { whitespace: true, message: "Invoice number cannot be empty" },
                            ]}
                        >
                            <Input placeholder="Enter invoice number" />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="startDate"
                                    label={
                                        <span>
                                            Start Date <span style={{ color: "red" }}>*</span>
                                        </span>
                                    }
                                    rules={[
                                        { required: true, message: "Please select start date" },
                                    ]}
                                >
                                    <Input
                                        type="date"
                                        placeholder="Select start date"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="endDate"
                                    label={
                                        <span>
                                            End Date <span style={{ color: "red" }}>*</span>
                                        </span>
                                    }
                                    rules={[{ required: true, message: "Please select end date" }]}
                                >
                                    <Input
                                        type="date"
                                        placeholder="Select end date"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label={
                                <span>
                                    Upload File <span style={{ color: "red" }}>*</span>
                                </span>
                            }
                            required
                        >
                            <Input
                                type="file"
                                onChange={handleFileUpload}
                                accept=".pdf,.jpg,.jpeg,.png"
                            />
                            {uploadedFile && (
                                <div style={{ marginTop: "8px", color: "#52c41a" }}>
                                    Selected: {uploadedFile.name} (
                                    {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                                </div>
                            )}
                        </Form.Item>

                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={uploadingInvoice}
                                    style={{ backgroundColor: "#004f7c", borderColor: "#004f7c" }}
                                >
                                    Upload Invoice
                                </Button>
                                <Button onClick={handleUploadModalCancel}>Cancel</Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );
}

export default Invoices;